import { Component, Injector, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { DownloadService, forLifeOf, LifecycleAware, Modal } from '@lsq-frontend/common-ui';
import { getRouterLinkById, ResponsiveService } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { LsqGroupedDataTableComponent } from '@lsq-frontend/common-ui';
import { of } from 'rxjs';
import { AnalyticsEvent, AnalyticsValue } from '../../../../../constants/analytics.constants';

import { CUSTOMER_COLLECTIONS, customerAtlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { ByCustomerDateRangeReportComponent } from '../../../../reports/base/by-customer-date-range-report.component';
import {
    CollectionCheck,
    GetCheckArchiveUrlsRequest,
    GetCheckArchiveUrlsService,
    GetCollectionChecksByRelationshipRequest,
    GetCollectionChecksByRelationshipResponse,
    GetCollectionChecksByRelationshipService,
    GetCollectionInvoicesByRelationshipRangeService,
    ListCheckKeysByRelationshipRequest,
    ListCheckKeysByRelationshipService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { ReportSavedDateFilterNames } from '../../../../services/saved-date-filter.service';
import { CustomerDetailUserStateTypes } from '../../customer-detail.user-state-types';
import { FileType } from '@lsq-frontend/remote-services/enterprise/common';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@LifecycleAware()
@Component({
    selector: 'collections-by-customer-report',
    template: `
        <div id="collections-report-page-container" class="container-fluid page-container">
            <div class="table" *ngIf="dataSource">
                <collections-grouped-table
                    #tableComponent
                    [dataSource]="dataSource"
                    [groupBy]="groupCollectionsBy"
                    [rowPrimaryKey]="'checkKey'"
                    [rowsAreCheckable]="true"
                    [getKeysByDateBaseRequest]="listCheckKeysByRelationshipRequest"
                    [getKeysByDateService]="listCheckKeysByRelationshipService"
                    (requestChange)="dataSource.dispatchRequest($event)"
                    (rowCheck)="handleRowCheck($event)"
                >
                </collections-grouped-table>
            </div>
        </div>

        <ng-template #bannerContentTemplate>
            <lsq-button
                *ngIf="rowsChecked.length && !(responsiveService.smBelow | async)"
                class="check-download-btn"
                [style.margin-right.rem]="1.5"
                [submitting]="downloadingChecks"
                (buttonClick)="handleDownloadChecks()"
            >
                Download Checks ({{ rowsChecked.length }})
            </lsq-button>
        </ng-template>
    `
})
export class CollectionsByCustomerReportComponent extends ByCustomerDateRangeReportComponent
    implements OnDestroy {
    @ViewChild('bannerContentTemplate') public bannerContentTemplate: TemplateRef<any>;
    @ViewChild('tableComponent') public tableComponent: LsqGroupedDataTableComponent;
    public title = 'Collections';
    public groupCollectionsBy = 'paymentDate';
    public analyticsName = AnalyticsValue.CollectionsByCustomer;
    public userStateType = CustomerDetailUserStateTypes.GetCollectionChecksByRelationshipRequest;
    public savedDateFilterName = ReportSavedDateFilterNames.CollectionsByCustomerReport;

    public listCheckKeysByRelationshipRequest: ListCheckKeysByRelationshipRequest;
    public rowsChecked: string[] = [];
    public downloadingChecks = false;
    public downloadTimeout: any;

    public dataSource = new LsqApiServiceListSource<
        GetCollectionChecksByRelationshipResponse,
        GetCollectionChecksByRelationshipRequest,
        CollectionCheck[]
    >(this.apiService);

    constructor(
        private _getCheckArchiveUrlsService: GetCheckArchiveUrlsService,
        private _downloadService: DownloadService,
        private _modalService: ModalService,
        private _appSettings: AppSettings,
        private _alertService: AlertService,
        public apiService: GetCollectionChecksByRelationshipService,
        public analyticsService: AnalyticsService,
        public responsiveService: ResponsiveService,
        public getCollectionInvoicesByRelationshipRangeService: GetCollectionInvoicesByRelationshipRangeService,
        public listCheckKeysByRelationshipService: ListCheckKeysByRelationshipService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();

        this.customerDetailService.bannerDataStream
            .pipe(forLifeOf(this))
            .subscribe((bannerData) => {
                this.listCheckKeysByRelationshipRequest = new ListCheckKeysByRelationshipRequest({
                    debtorKey: bannerData.debtorKey
                });
            });
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public handleRowCheck(rows?: string[]): void {
        this.rowsChecked = rows;
    }

    public updateCustomerDetailBanner(): void {
        this.getCollectionInvoicesByRelationshipRangeService.setRequestStream(
            of(this.dataSource.request)
        );

        this.customerDetailService.updateBanner({
            debtorKey: this.debtorKey,
            debtorName: this.debtorName,
            lastUpdatedDate: this.lastUpdatedDate,
            title: this.debtorName,
            reportName: `${this.title} ${this.debtorName}`,
            reportId: getRouterLinkById(customerAtlas, CUSTOMER_COLLECTIONS, {
                relative: true
            }).join('/'),
            analyticsName: this.analyticsName,
            apiService: this.getCollectionInvoicesByRelationshipRangeService,
            datePickerConfiguration: this.datePickerConfiguration,
            rightContentTemplate: {
                key: Symbol('download-check-button'),
                template: this.bannerContentTemplate
            }
        });
    }

    public handleDownloadChecks(): void {
        if (this.rowsChecked.length > this._appSettings.maximumImageDownloadSize) {
            this._modalService.showModal(
                new Modal({
                    id: 'check-download-too-large',
                    title: 'Too many checks selected',
                    bodyText: `Too many checks are selected; please select less than ${this._appSettings.maximumImageDownloadSize} checks.`,
                    showFooter: true,
                    showOkButton: true,
                    okButtonText: 'Ok',
                    onOkClick: () => {
                        this._modalService.closeModal();
                    }
                })
            );
        } else if (this.rowsChecked.length > this._appSettings.warningImageDownloadSize) {
            this._modalService.showModal(
                new Modal({
                    id: 'check-download-warning',
                    title: 'Download may take a long time',
                    bodyText:
                        'A large amount of checks have been selected. Your download may take a while. Would you like to continue?',
                    showFooter: true,
                    showCancelButton: true,
                    showOkButton: true,
                    okButtonText: 'Continue',
                    onOkClick: () => {
                        this.downloadingChecks = true;
                        this.downloadChecks();
                        this._modalService.closeModal();
                    }
                })
            );
        } else {
            this.downloadingChecks = true;
            this.downloadChecks();
        }
        this.analyticsService.triggerSimpleEvent(AnalyticsEvent.DownloadChecks);
    }

    public async downloadChecks(): Promise<void> {
        this._getCheckArchiveUrlsService
            .executeOnce(
                new GetCheckArchiveUrlsRequest({
                    checkKeys: this.rowsChecked.map((row) =>
                        parseInt(row.substring(row.indexOf('-') + 1))
                    )
                })
            )
            .then((response) => {
                const dateStamp = new Date().getTime();

                response.urls.forEach((url, index) => {
                    this._downloadService.executeUrl(
                        url,
                        FileType.Zip,
                        `checks-${dateStamp}-${index}`
                    );
                });
                clearTimeout(this.downloadTimeout);
                if (!this.downloadingChecks) {
                    // If the dropdown is already closed, show a success toast.
                    this._alertService.success(
                        'Your check images have been successfully generated.'
                    );
                }
                this.downloadingChecks = false;
                this.tableComponent.handleSelectAll(false);
            })
            .catch((error) => {
                // On error no longer show loading indicator.
                this.downloadingChecks = false;
                clearTimeout(this.downloadTimeout);
            });

        // If generating the report takes more than 10 sec, close and show the info toast.
        this.downloadTimeout = setTimeout(() => {
            this._showGeneratingMessage();
            this.downloadingChecks = false;
        }, this._appSettings.downloadBackgroundTimeout);
    }

    private _showGeneratingMessage(): void {
        this._alertService.info(
            'Collecting your check images can take several minutes. You can continue using the application, but refrain from closing/refreshing the page.',
            {
                title: 'Check Images Generating',
                config: {
                    disableTimeOut: true
                }
            }
        );
    }
}
